import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/store';
import { Link2, Plus, Trash2, Search, Loader2, X } from 'lucide-react';

const brl = (v) => (v == null ? '' : `R$ ${Number(v).toFixed(2).replace('.', ',')}`);
const PROVIDER_TINT = {
  sigma: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  painelfoda: 'bg-sky-500/15 text-sky-400 border-sky-500/25',
  geradorpro: 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/25',
  fshorts: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25',
};

export default function LinkedAccountsSection({ customerId, onChanged }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // form de adicionar
  const [sources, setSources] = useState([]);
  const [sourceKey, setSourceKey] = useState('');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [plans, setPlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);

  const load = async () => {
    try {
      const [r, rp] = await Promise.all([
        api.get(`/customers/${customerId}/accounts`),
        api.get('/customer-plans'),
      ]);
      setAccounts(r.data.accounts || []);
      setAllPlans(rp.data.plans || rp.data || []);
    } catch { setAccounts([]); }
    setLoading(false);
  };

  const changeAccountPlan = async (accountId, planId) => {
    try {
      await api.put(`/customers/${customerId}/accounts/${accountId}/plan`, { plan_id: planId });
      toast.success('Plano do acesso atualizado');
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao mudar plano'); }
  };
  const syncPhoneAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/customers/${customerId}/sync-phone-all`);
      toast.success(r.data.message || 'Telefone sincronizado em todos os painéis');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao sincronizar'); }
    finally { setSyncing(false); }
  };
  useEffect(() => { load(); }, [customerId]);

  const openAdd = async () => {
    setAdding(true); setResults([]); setQuery('');
    try {
      const [rs, rp] = await Promise.all([api.get('/imports/sources'), api.get('/customer-plans')]);
      const list = rs.data.sources || [];
      setSources(list);
      if (list.length) setSourceKey(`${list[0].type}:${list[0].id}`);
      setPlans(rp.data.plans || rp.data || []);
    } catch { /* ignore */ }
  };

  const source = sources.find((s) => `${s.type}:${s.id}` === sourceKey);

  const search = async () => {
    if (!source) return;
    setSearching(true); setResults([]);
    try {
      const r = await api.post(`/sigma/login/${source.id}/search-customers`, { query: query.trim(), only_mine: true });
      setResults(r.data.customers || []);
      if ((r.data.customers || []).length === 0) toast.warning('Nenhum usuário encontrado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao buscar'); }
    finally { setSearching(false); }
  };

  const plansForSource = () => plans.filter((p) => p.active !== false && p.sigma_login_integration_id === source?.id);

  const addAccount = async (panelCustomer) => {
    // tenta achar um plano da integração + pacote; senão o primeiro da integração
    const ps = plansForSource();
    let plan = ps.find((p) => p.sigma_login_package_id === panelCustomer.package_id) || ps[0];
    try {
      await api.post(`/customers/${customerId}/accounts`, {
        sigma_login_integration_id: source.id,
        panel_customer_id: String(panelCustomer.panel_customer_id),
        sigma_username: panelCustomer.username,
        panel_package_id: panelCustomer.package_id || '',
        panel_connections: panelCustomer.connections || 1,
        plan_id: plan?.id || null,
        provider: source.provider,
      });
      toast.success(`${panelCustomer.username} vinculado!`);
      setAdding(false);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao vincular'); }
  };

  const removeAccount = async (accountId, label) => {
    if (!window.confirm(`Remover o acesso "${label}"? (não exclui no painel, só desvincula aqui)`)) return;
    try {
      await api.delete(`/customers/${customerId}/accounts/${accountId}`);
      toast.success('Acesso removido');
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao remover'); }
  };

  if (loading) return <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;

  const totalPrice = accounts.reduce((sum, a) => sum + (Number(a.plan_price) || 0), 0);

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Acessos vinculados</span>
          <span className="text-[10px] bg-primary/15 text-primary rounded-full px-2 py-0.5">{accounts.length}</span>
        </div>
        {accounts.length > 0 && totalPrice > 0 && (
          <span className="text-xs text-muted-foreground">Soma dos planos: <b className="text-foreground">{brl(totalPrice)}</b></span>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Um cliente pode ter vários usuários de painéis diferentes (ex.: Sigma + PainelFoda). Ao renovar, todos são renovados juntos.
      </p>

      {/* Lista de acessos */}
      {accounts.length > 0 ? (
        <div className="space-y-1.5">
          {accounts.map((a) => (
            <div key={a.account_id} className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/40 px-2.5 py-2">
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${PROVIDER_TINT[a.provider] || 'bg-muted text-muted-foreground'}`}>
                {a.provider_label || 'Painel'}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{a.sigma_username || a.label || '—'}</p>
                <p className="text-[11px] text-muted-foreground truncate">{a.integration_name}</p>
                <Select value={a.plan_id || ''} onValueChange={(v) => changeAccountPlan(a.account_id, v)}>
                  <SelectTrigger className="h-7 mt-1 text-xs"><SelectValue placeholder="Escolher plano" /></SelectTrigger>
                  <SelectContent>
                    {allPlans.filter((p) => p.active !== false && p.sigma_login_integration_id === a.sigma_login_integration_id).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} — {brl(p.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 shrink-0"
                onClick={() => removeAccount(a.account_id, a.sigma_username || a.label)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-2">Nenhum acesso vinculado ainda.</p>
      )}

      {accounts.length > 1 && (
        <Button variant="outline" size="sm" className="w-full" onClick={syncPhoneAll} disabled={syncing}>
          {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sincronizando…</> : '📱 Sincronizar telefone em todos os painéis'}
        </Button>
      )}

      {/* Adicionar */}
      {!adding ? (
        <Button variant="outline" size="sm" className="w-full" onClick={openAdd}>
          <Plus className="w-4 h-4 mr-1.5" /> Vincular outro usuário (importar)
        </Button>
      ) : (
        <div className="rounded-lg border border-border/60 p-2.5 space-y-2 bg-background/40">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Importar usuário</span>
            <button onClick={() => setAdding(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <Select value={sourceKey} onValueChange={setSourceKey}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Escolha o painel" /></SelectTrigger>
            <SelectContent>
              {sources.map((s) => <SelectItem key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.name} — {s.provider_label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input className="h-9" placeholder="Usuário no painel" value={query}
              onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} />
            <Button size="sm" onClick={search} disabled={searching}>
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {results.length > 0 && (
            <div className="max-h-44 overflow-y-auto space-y-1">
              {results.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-border/50 px-2.5 py-1.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.username}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{c.package_name || '—'}{c.expiration_date ? ` · vence ${c.expiration_date}` : ''}{c.already_imported ? ' · já vinculado' : ''}</p>
                  </div>
                  <Button size="sm" className="h-7" onClick={() => addAccount(c)}>Vincular</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
